// src/ConsumerApp/Screens/ProductDetails/ProductDetailsScreen.js

import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../../DesignSystem/Navigation/Navbar';
import CenteredContainer from '../../DesignSystem/Layout/CenteredContainer';

// Real SVGs from assets/icons
import SealCheckRed from '../../../assets/icons/SealCheckRed.svg';
import FileText from '../../../assets/icons/FileText.svg';
import TruckRed from '../../../assets/icons/TruckRed.svg';
import WhitePlus from '../../../assets/icons/WhitePlus.svg';
import WhiteMinus from '../../../assets/icons/WhiteMinus.svg';
import CaretDownRed from '../../../assets/icons/CaretDownRed.svg';
import CaretUpRed from '../../../assets/icons/CaretUpRed.svg';
import WhatsappLogo from '../../../assets/icons/WhatsappLogo.svg';
import BookmarkIcon from '../../../assets/icons/bookmark.svg';
import FillBookmarkIcon from '../../../assets/icons/FillBookmark.svg';
import ToteIcon from '../../../assets/icons/tote.svg';
import X from '../../../assets/icons/X.svg';

// Custom Hooks
import {
  useGetProductById,
  useGetFaqsBySubCategoryId,
  useGetBucketSummary,
  useAddItemToBucket,
  useUpdateBucketItemQuantity,
  useDeleteBucketItem,
  useGetRelatedProducts,
  useGetProductVariants,
} from '../../api/products/product.hooks';
import {
  useGetSavedList,
  useAddItemToSavedList,
  useRemoveItemFromSavedList,
} from '../../api/savedList/savedList.hooks';

const IMAGE_URL =
  process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES || 'https://assets.brickbucket.in/';
const BUSINESS_WHATSAPP_NUMBER = '918639065454';

export default function ProductDetailsScreen() {
  const navigate = useNavigate();
  const location = useLocation();

  // 1. Resolve Product ID from Route State or Query Params
  const queryParams = new URLSearchParams(location.search);
  const routeProductId =
    location.state?.productId ||
    location.state?.item?.product_id ||
    location.state?.item?._id ||
    queryParams.get('id');

  const [activeProductId, setActiveProductId] = useState(routeProductId);

  // 2. Session Context (Direct Browser Storage)
  const customer = useMemo(() => {
    try {
      const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, []);

  const customerId = customer?._id || customer?.id;
  const hubId = customer?.hubId || '';

  // 3. API Queries
  const { data: productRes, isLoading: isProductLoading } = useGetProductById(activeProductId);
  const productData = productRes?.productData || productRes;
  const subCategoryId = productData?.product_sub_category_id;

  const { data: faqs = [] } = useGetFaqsBySubCategoryId(subCategoryId);
  const { data: relatedProducts = [] } = useGetRelatedProducts({
    customer_id: customerId,
    product_id: activeProductId,
  });

  const { data: bucketSummary } = useGetBucketSummary(customerId);
  const { data: savedList = [] } = useGetSavedList(customerId);

  // Mutations
  const addItemMutation = useAddItemToBucket();
  const updateItemMutation = useUpdateBucketItemQuantity();
  const deleteItemMutation = useDeleteBucketItem();
  const addSavedItemMutation = useAddItemToSavedList();
  const removeSavedItemMutation = useRemoveItemFromSavedList();
  const { mutateAsync: fetchProductVariants } = useGetProductVariants();

  // 4. Gallery & View State
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [mainImgError, setMainImgError] = useState(false);
  const [thumbErrors, setThumbErrors] = useState({});
  const [expandedFaqId, setExpandedFaqId] = useState(null);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [variantsList, setVariantsList] = useState([]);
  const [isLoadingVariants, setIsLoadingVariants] = useState(false);

  // 5. Bucket & Saved Map Lookup
  const isSaved = useMemo(() => {
    return savedList.some(
      (item) => String(item.product_id || item.item_id || item._id) === String(activeProductId)
    );
  }, [savedList, activeProductId]);

  const bucketItemsMap = useMemo(() => {
    const map = new Map();
    const buyNowItems = bucketSummary?.buy_now_bucket?.items || [];
    buyNowItems.forEach((bItem) => {
      if (bItem?.item_id) {
        map.set(String(bItem.item_id), {
          quantity: Number(bItem.quantity) || 1,
          bucketId: bucketSummary?.buy_now_bucket?.bucket_id || bucketSummary?.buy_now_bucket?._id,
        });
      }
    });
    return map;
  }, [bucketSummary]);

  const currentCartEntry = bucketItemsMap.get(String(activeProductId));
  const inCartQty = currentCartEntry ? currentCartEntry.quantity : 0;

  // 6. Product Fields
  const name = productData?.product_name || 'Construction Material';
  const unitText = productData?.uom || productData?.UOM || 'Unit';
  const buyNowPrice = Number(productData?.buy_now?.unit_price || productData?.selling_price || 0);
  const mrpPrice = Number(productData?.mrp || 0);
  const discountText = productData?.discount ? `${productData.discount}% off` : '';
  const hasOptions = Number(productRes?.other_options_count || 0) > 0;

  const productImages = useMemo(() => {
    if (productData?.images && productData.images.length > 0) {
      return productData.images.map((img) =>
        img.startsWith('http') ? img : `${IMAGE_URL}${img}`
      );
    }
    const singleImg = productData?.product_image_url || productData?.product_image;
    if (singleImg) {
      return [singleImg.startsWith('http') ? singleImg : `${IMAGE_URL}${singleImg}`];
    }
    return [];
  }, [productData]);

  // 7. Handlers
  const handleToggleSave = (e) => {
    e.stopPropagation();
    if (!customerId) {
      navigate('/consumer');
      return;
    }
    if (isSaved) {
      removeSavedItemMutation.mutate({ customer_id: customerId, product_id: activeProductId });
    } else {
      addSavedItemMutation.mutate({ customer_id: customerId, product_id: activeProductId });
    }
  };

  const handleOpenVariantsModal = async () => {
    setIsLoadingVariants(true);
    try {
      const response = await fetchProductVariants({
        product_id: activeProductId,
        purchase_mode: 'Buy Now',
      });
      setVariantsList(Array.isArray(response) ? response : response?.product_variants || []);
      setShowOptionsModal(true);
    } catch (err) {
      console.error('Error fetching variants:', err);
    } finally {
      setIsLoadingVariants(false);
    }
  };

  const handleInitialAdd = () => {
    if (!customerId) {
      navigate('/consumer');
      return;
    }
    if (hasOptions) {
      handleOpenVariantsModal();
      return;
    }

    const minQty = productData?.buy_now?.min_qty || 1;
    addItemMutation.mutate({
      customer_id: customerId,
      hubId,
      item_id: activeProductId,
      purchase_type: 'BUY_NOW',
      quantity: minQty,
    });
  };

  const handleUpdateQuantity = (newQty) => {
    if (newQty <= 0) {
      deleteItemMutation.mutate({
        customer_id: customerId,
        item_id: activeProductId,
        bucket_id: currentCartEntry?.bucketId,
      });
    } else {
      updateItemMutation.mutate({
        customer_id: customerId,
        hubId,
        item_id: activeProductId,
        purchase_type: 'BUY_NOW',
        quantity: newQty,
      });
    }
  };

  const handleWhatsAppQuote = () => {
    const message = encodeURIComponent(
      `Hi, I would like to request price quotation for: ${name} (Product ID: ${activeProductId})`
    );
    window.open(`https://wa.me/${BUSINESS_WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };

  const parsedSpecifications = useMemo(() => {
    const specs = productData?.specifications;
    if (!specs) return [];
    try {
      if (Array.isArray(specs)) {
        return specs.filter((item) => item && typeof item === 'object' && !Array.isArray(item));
      }
      if (typeof specs === 'string') {
        const parsed = JSON.parse(specs.trim());
        return Array.isArray(parsed) ? parsed : [];
      }
      if (typeof specs === 'object') return [specs];
      return [];
    } catch {
      return [];
    }
  }, [productData?.specifications]);

  if (isProductLoading) {
    return (
      <div style={styles.pageShell}>
        <Navbar />
        <div style={styles.loadingBox}>
          <span>Loading product details...</span>
        </div>
      </div>
    );
  }

  const currentMainImg = productImages[selectedImageIndex] || productImages[0];

  return (
    <div style={styles.pageShell}>
      <Navbar />

      <CenteredContainer>
        <div style={styles.contentStack}>
          {/* Breadcrumbs Row */}
          <div style={styles.breadcrumbRow}>
            <span style={styles.breadcrumbLink} onClick={() => navigate('/consumer/HomeScreen')}>
              Home
            </span>
            <span style={styles.breadcrumbDivider}>/</span>
            <span style={styles.breadcrumbLink} onClick={() => navigate('/consumer/productslist')}>
              Products
            </span>
            <span style={styles.breadcrumbDivider}>/</span>
            <span style={styles.breadcrumbCurrent}>{name}</span>
          </div>

          {/* ── Main Dual-Column Product Hero ── */}
          <div style={styles.heroGrid}>
            {/* Left Column: Multi-Image Desktop Gallery */}
            <div style={styles.galleryColumn}>
              <div style={styles.mainImageWrapper}>
                {currentMainImg && !mainImgError ? (
                  <img
                    src={currentMainImg}
                    alt={name}
                    style={styles.mainImg}
                    onError={() => setMainImgError(true)}
                  />
                ) : (
                  <div style={styles.toteFallbackWrapper}>
                    <img
                      src={ToteIcon}
                      alt="Material placeholder"
                      style={styles.toteFallbackIcon}
                    />
                  </div>
                )}

                <button
                  type="button"
                  style={styles.bookmarkFloatBtn}
                  onClick={handleToggleSave}
                  title={isSaved ? 'Remove from Shortlist' : 'Save for Later'}
                >
                  <img
                    src={isSaved ? FillBookmarkIcon : BookmarkIcon}
                    alt="bookmark"
                    style={{
                      width: 18,
                      height: 18,
                      filter: isSaved
                        ? 'invert(16%) sepia(85%) saturate(6120%) hue-rotate(348deg) brightness(91%) contrast(106%)'
                        : 'none',
                    }}
                  />
                </button>
              </div>

              {/* Thumbnails Row */}
              {productImages.length > 1 && (
                <div style={styles.thumbnailsRow}>
                  {productImages.map((imgUrl, idx) => {
                    const isSelected = selectedImageIndex === idx;
                    const hasThumbError = thumbErrors[idx];

                    return (
                      <div
                        key={idx}
                        style={{
                          ...styles.thumbWrapper,
                          borderColor: isSelected ? '#C7061F' : '#E2E8F0',
                          boxShadow: isSelected ? '0 0 0 1px #C7061F' : 'none',
                        }}
                        onClick={() => {
                          setSelectedImageIndex(idx);
                          setMainImgError(false);
                        }}
                      >
                        {imgUrl && !hasThumbError ? (
                          <img
                            src={imgUrl}
                            alt={`thumb-${idx}`}
                            style={styles.thumbImg}
                            onError={() =>
                              setThumbErrors((prev) => ({ ...prev, [idx]: true }))
                            }
                          />
                        ) : (
                          <img
                            src={ToteIcon}
                            alt="fallback"
                            style={{ width: 22, height: 22, opacity: 0.35 }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Right Column: Pricing, Buy Actions & Specifications */}
            <div style={styles.detailsColumn}>
              <div style={styles.headerCluster}>
                <span style={styles.categoryBadge}>
                  {productData?.product_category_name || 'Building Material'}
                </span>
                <h1 style={styles.productTitleText}>{name}</h1>
                <span style={styles.unitMetaText}>Unit of Measure: {unitText}</span>
              </div>

              {/* Price & Discount Bar */}
              <div style={styles.priceContainer}>
                <div style={styles.priceRow}>
                  <span style={styles.sellingPriceText}>₹{buyNowPrice.toLocaleString('en-IN')}</span>
                  {mrpPrice > buyNowPrice && (
                    <span style={styles.mrpStrikeText}>₹{mrpPrice.toLocaleString('en-IN')}</span>
                  )}
                  {discountText && <span style={styles.discountPill}>{discountText}</span>}
                </div>
                <span style={styles.taxInclusiveText}>Inclusive of all GST & site delivery loading</span>
              </div>

              {/* Compact, Professional Action Row */}
              <div style={styles.actionRow}>
                {inCartQty > 0 ? (
                  /* Zepto-Style Solid Red Stepper with 16px White Icons */
                  <div style={styles.qtyStepper}>
                    <button
                      type="button"
                      style={styles.stepperBtn}
                      onClick={() => handleUpdateQuantity(inCartQty - 1)}
                      aria-label="Decrease quantity"
                    >
                      <img src={WhiteMinus} alt="minus" style={{ width: 16, height: 16 }} />
                    </button>
                    <span style={styles.stepperCount}>{inCartQty}</span>
                    <button
                      type="button"
                      style={styles.stepperBtn}
                      onClick={() => handleUpdateQuantity(inCartQty + 1)}
                      aria-label="Increase quantity"
                    >
                      <img src={WhitePlus} alt="plus" style={{ width: 16, height: 16 }} />
                    </button>
                  </div>
                ) : (
                  /* Professional Compact CTA Button */
                  <button
                    type="button"
                    style={styles.addToBucketBtn}
                    onClick={handleInitialAdd}
                    disabled={isLoadingVariants}
                  >
                    {hasOptions
                      ? `Select Options (${productRes?.other_options_count})`
                      : 'Add to Bucket'}
                  </button>
                )}

                <button
                  type="button"
                  style={styles.whatsAppQuoteBtn}
                  onClick={handleWhatsAppQuote}
                >
                  <img src={WhatsappLogo} alt="whatsapp" style={{ width: 16, height: 16 }} />
                  <span>Custom Quote</span>
                </button>
              </div>

              {/* Trust Badges Bar */}
              <div style={styles.trustBadgesGrid}>
                <div style={styles.trustBadgeItem}>
                  <img src={SealCheckRed} alt="seal" style={{ width: 16, height: 16 }} />
                  <span>Verified Grade</span>
                </div>
                <div style={styles.trustBadgeItem}>
                  <img src={FileText} alt="gst" style={{ width: 16, height: 16 }} />
                  <span>GST Tax Invoice</span>
                </div>
                <div style={styles.trustBadgeItem}>
                  <img src={TruckRed} alt="truck" style={{ width: 16, height: 16 }} />
                  <span>Direct Site Dispatch</span>
                </div>
              </div>

              {/* Specifications Table */}
              <div style={styles.specsCard}>
                <h3 style={styles.specsTitle}>Specifications</h3>
                {parsedSpecifications.length > 0 ? (
                  <div style={styles.specsTable}>
                    {parsedSpecifications.map((spec, idx) => {
                      const [label, value] = Object.entries(spec)[0] || ['N/A', 'N/A'];
                      return (
                        <div key={idx} style={styles.specsTableRow}>
                          <span style={styles.specsLabel}>{label}</span>
                          <span style={styles.specsValue}>{String(value)}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <span style={styles.noSpecsText}>Standard industrial specifications apply.</span>
                )}
              </div>
            </div>
          </div>

          {/* ── Bulk Requirement WhatsApp Banner ── */}
          <div style={styles.bulkRfqBanner}>
            <div style={styles.bulkRfqLeft}>
              <div style={styles.whatsappCircle}>
                <img src={WhatsappLogo} alt="whatsapp" style={{ width: 22, height: 22 }} />
              </div>
              <div style={styles.bulkRfqTextGroup}>
                <strong style={styles.bulkRfqTitle}>Got high-volume site requirements?</strong>
                <span style={styles.bulkRfqSubtitle}>
                  Order directly in trailer loads or batch quantities with dedicated enterprise rates.
                </span>
              </div>
            </div>
            <button
              type="button"
              style={styles.bulkRfqActionBtn}
              onClick={handleWhatsAppQuote}
            >
              Request Commercial Quote
            </button>
          </div>

          {/* ── Related Products Row ── */}
          {relatedProducts.length > 0 && (
            <div style={styles.productsCarouselSection}>
              <h3 style={styles.sectionHeading}>Frequently Bought Together</h3>
              <div style={styles.productsRow}>
                {relatedProducts.slice(0, 4).map((item, idx) => {
                  const pId = String(item._id || item.product_id);
                  const pName = item.product_name || 'Material Item';
                  const pPrice = Number(item.selling_price || item.buy_now?.unit_price || 0);
                  const pImg = item.product_image_url || item.product_image;

                  return (
                    <div
                      key={idx}
                      style={styles.miniProductCard}
                      onClick={() => {
                        setActiveProductId(pId);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    >
                      <div style={styles.miniImgWrapper}>
                        {pImg ? (
                          <img
                            src={pImg.startsWith('http') ? pImg : `${IMAGE_URL}${pImg}`}
                            alt={pName}
                            style={styles.miniImg}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              if (e.currentTarget.nextSibling) {
                                e.currentTarget.nextSibling.style.display = 'flex';
                              }
                            }}
                          />
                        ) : null}
                        <div
                          style={{
                            ...styles.miniFallbackTote,
                            display: pImg ? 'none' : 'flex',
                          }}
                        >
                          <img
                            src={ToteIcon}
                            alt="fallback"
                            style={{ width: 26, height: 26, opacity: 0.35 }}
                          />
                        </div>
                      </div>
                      <span style={styles.miniTitle} title={pName}>
                        {pName}
                      </span>
                      <strong style={styles.miniPrice}>₹{pPrice.toLocaleString('en-IN')}</strong>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── FAQs Accordion Section ── */}
          {faqs.length > 0 && (
            <div style={styles.faqsSection}>
              <h3 style={styles.sectionHeading}>Frequently Asked Questions</h3>
              <div style={styles.faqsStack}>
                {faqs.map((faq) => {
                  const faqId = faq._id || faq.id;
                  const isExpanded = expandedFaqId === faqId;

                  return (
                    <div key={faqId} style={styles.faqItem}>
                      <button
                        type="button"
                        style={styles.faqTrigger}
                        onClick={() => setExpandedFaqId(isExpanded ? null : faqId)}
                      >
                        <span style={styles.faqQuestion}>{faq.question}</span>
                        <img
                          src={isExpanded ? CaretUpRed : CaretDownRed}
                          alt="toggle"
                          style={{ width: 14, height: 14 }}
                        />
                      </button>
                      {isExpanded && <p style={styles.faqAnswer}>{faq.answer}</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </CenteredContainer>

      {/* ── Variant Options Modal ── */}
      {showOptionsModal && (
        <div style={styles.modalOverlay} onClick={() => setShowOptionsModal(false)}>
          <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <h4 style={styles.modalTitle}>Select Product Specification</h4>
                <span style={styles.modalSub}>{variantsList.length} options available</span>
              </div>
              <button
                type="button"
                style={styles.closeBtn}
                onClick={() => setShowOptionsModal(false)}
              >
                <img src={X} alt="close" style={{ width: 16, height: 16 }} />
              </button>
            </div>

            <div style={styles.modalVariantsList}>
              {variantsList.map((variant, idx) => {
                const vId = variant.product_id || variant._id || `var-${idx}`;
                const vTitle = variant.product_name || variant.title || 'Specification Option';
                const vPrice = Number(variant.selling_price || variant.price || 0);

                return (
                  <div key={vId} style={styles.variantItemRow}>
                    <div style={styles.variantDetailsCol}>
                      <strong style={styles.variantTitleText}>{vTitle}</strong>
                      <span style={styles.variantUnitText}>
                        {variant.UOM || variant.unitLabel || 'Standard Pack'}
                      </span>
                      <strong style={styles.variantPriceText}>₹{vPrice.toLocaleString('en-IN')}</strong>
                    </div>

                    <button
                      type="button"
                      style={styles.selectVariantBtn}
                      onClick={() => {
                        setActiveProductId(vId);
                        setShowOptionsModal(false);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    >
                      Select
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  pageShell: {
    backgroundColor: '#FAF9F6',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  loadingBox: {
    padding: 100,
    textAlign: 'center',
    color: '#707B8C',
    fontSize: 14,
  },
  contentStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 28,
    padding: '24px 0 60px 0',
  },
  breadcrumbRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
  },
  breadcrumbLink: {
    color: '#64748B',
    cursor: 'pointer',
    fontWeight: '500',
  },
  breadcrumbDivider: {
    color: '#CBD5E1',
  },
  breadcrumbCurrent: {
    color: '#2B2A29',
    fontWeight: '700',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 400,
  },
  heroGrid: {
    display: 'grid',
    gridTemplateColumns: '460px 1fr',
    gap: 40,
    alignItems: 'flex-start',
  },
  galleryColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    position: 'sticky',
    top: 96,
  },
  mainImageWrapper: {
    height: 420,
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    border: '1px solid #CBD5E1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    position: 'relative',
    overflow: 'hidden',
  },
  mainImg: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
  },
  toteFallbackWrapper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F8FAFC',
    borderRadius: 12,
  },
  toteFallbackIcon: {
    width: 64,
    height: 64,
    opacity: 0.3,
  },
  bookmarkFloatBtn: {
    position: 'absolute',
    top: 16,
    right: 16,
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: '#FFFFFF',
    border: '1px solid #E2E8F0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
  },
  thumbnailsRow: {
    display: 'flex',
    gap: 12,
    overflowX: 'auto',
    paddingBottom: 4,
  },
  thumbWrapper: {
    width: 72,
    height: 72,
    borderRadius: 10,
    backgroundColor: '#FFFFFF',
    border: '1.5px solid #E2E8F0',
    padding: 6,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  thumbImg: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
  },
  detailsColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  headerCluster: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  categoryBadge: {
    fontSize: 11.5,
    fontWeight: '700',
    color: '#C7061F',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  productTitleText: {
    margin: 0,
    fontSize: 26,
    fontWeight: '800',
    color: '#2B2A29',
    lineHeight: '34px',
  },
  unitMetaText: {
    fontSize: 13,
    color: '#707B8C',
  },
  priceContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    border: '1px solid #CBD5E1',
    padding: '16px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  priceRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  sellingPriceText: {
    fontSize: 28,
    fontWeight: '800',
    color: '#2B2A29',
  },
  mrpStrikeText: {
    fontSize: 16,
    color: '#94A3B8',
    textDecoration: 'line-through',
  },
  discountPill: {
    backgroundColor: '#FEF2F2',
    color: '#C7061F',
    border: '1px solid #FECDD3',
    padding: '2px 8px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: '700',
  },
  taxInclusiveText: {
    fontSize: 11.5,
    color: '#707B8C',
  },
  actionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginTop: 4,
  },
  addToBucketBtn: {
    width: 160,
    height: 42,
    backgroundColor: '#E9001E',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease, transform 0.1s ease',
    boxShadow: '0 2px 8px rgba(233, 0, 30, 0.25)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  qtyStepper: {
    width: 140,
    height: 42,
    backgroundColor: '#E9001E',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 4px',
    boxShadow: '0 2px 8px rgba(233, 0, 30, 0.25)',
    boxSizing: 'border-box',
  },
  stepperBtn: {
    width: 36,
    height: 36,
    background: 'transparent',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
    borderRadius: 6,
  },
  stepperCount: {
    fontSize: 15,
    fontWeight: '800',
    color: '#FFFFFF',
    userSelect: 'none',
  },
  whatsAppQuoteBtn: {
    height: 42,
    padding: '0 18px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #CBD5E1',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
    fontWeight: '700',
    color: '#2B2A29',
    cursor: 'pointer',
  },
  trustBadgesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 12,
  },
  trustBadgeItem: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E2E8F0',
    borderRadius: 8,
    padding: '10px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 12,
    fontWeight: '600',
    color: '#475569',
  },
  specsCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    border: '1px solid #CBD5E1',
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  specsTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: '700',
    color: '#2B2A29',
  },
  specsTable: {
    display: 'flex',
    flexDirection: 'column',
  },
  specsTableRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '10px 0',
    borderBottom: '1px solid #F1F5F9',
    fontSize: 13,
  },
  specsLabel: {
    color: '#707B8C',
  },
  specsValue: {
    fontWeight: '600',
    color: '#2B2A29',
  },
  noSpecsText: {
    fontSize: 13,
    color: '#707B8C',
  },
  bulkRfqBanner: {
    backgroundColor: '#ECFDF5',
    border: '1px solid #A7F3D0',
    borderRadius: 14,
    padding: '18px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  bulkRfqLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  whatsappCircle: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#25D366',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  bulkRfqTextGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  bulkRfqTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: '#065F46',
  },
  bulkRfqSubtitle: {
    fontSize: 12.5,
    color: '#047857',
  },
  bulkRfqActionBtn: {
    backgroundColor: '#C7061F',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 8,
    padding: '10px 18px',
    fontSize: 13,
    fontWeight: '700',
    cursor: 'pointer',
  },
  productsCarouselSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  sectionHeading: {
    margin: 0,
    fontSize: 18,
    fontWeight: '800',
    color: '#2B2A29',
  },
  productsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 16,
  },
  miniProductCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    border: '1px solid #CBD5E1',
    padding: 14,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    transition: 'transform 0.15s ease',
  },
  miniImgWrapper: {
    height: 120,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F8FAFC',
    borderRadius: 8,
    padding: 8,
    position: 'relative',
    overflow: 'hidden',
  },
  miniImg: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
  },
  miniFallbackTote: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  miniTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: '#2B2A29',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  miniPrice: {
    fontSize: 14,
    fontWeight: '700',
    color: '#C7061F',
  },
  faqsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  faqsStack: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    border: '1px solid #CBD5E1',
    padding: '8px 20px',
  },
  faqItem: {
    borderBottom: '1px solid #F1F5F9',
    padding: '14px 0',
  },
  faqTrigger: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    padding: 0,
  },
  faqQuestion: {
    fontSize: 14,
    fontWeight: '700',
    color: '#2B2A29',
  },
  faqAnswer: {
    margin: '10px 0 0 0',
    fontSize: 13,
    color: '#64748B',
    lineHeight: '20px',
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    zIndex: 99999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backdropFilter: 'blur(2px)',
    padding: 20,
  },
  modalCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    padding: '24px 28px',
    width: '100%',
    maxWidth: 480,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    maxHeight: '80vh',
    overflowY: 'auto',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottom: '1px solid #F1F5F9',
    paddingBottom: 12,
  },
  modalTitle: {
    margin: 0,
    fontSize: 17,
    fontWeight: '700',
    color: '#2B2A29',
  },
  modalSub: {
    fontSize: 12,
    color: '#707B8C',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
  },
  modalVariantsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  variantItemRow: {
    border: '1px solid #CBD5E1',
    borderRadius: 10,
    padding: '12px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  variantDetailsCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  variantTitleText: {
    fontSize: 13.5,
    color: '#2B2A29',
  },
  variantUnitText: {
    fontSize: 11.5,
    color: '#707B8C',
  },
  variantPriceText: {
    fontSize: 14,
    color: '#C7061F',
    marginTop: 2,
  },
  selectVariantBtn: {
    backgroundColor: '#FFF1F2',
    color: '#C7061F',
    border: '1px solid #FECDD3',
    padding: '7px 14px',
    borderRadius: 6,
    fontSize: 12.5,
    fontWeight: '700',
    cursor: 'pointer',
  },
};